import {
  AlertTriangle,
  Cake,
  Heart,
  Hourglass,
  Network,
  Rainbow,
  Table2,
  Tag,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { treeStats, type Count, type Fact, type FactTopic, type TreeStats } from '../../model/stats';
import type { Id } from '../../model/types';
import { PersonLink } from '../components/common';
import { setState, setView, useApp } from '../store';

/** A distribution of one or two people says nothing; it takes a few to show a shape. */
const enough = (data: Count[]) => data.reduce((n, d) => n + d.count, 0) >= 3;
const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const num = (n: number) => n.toLocaleString('en-US');
/** A search filter matching exactly what a bar counts (search's key:=value). */
const exactly = (key: string, value: string) => `${key}:"=${value.replace(/"/g, ' ')}"`;

/** Facts and charts about the tree: who's in it, when and where they lived, and how it all connects. */
export function ReportsView() {
  const tree = useApp((s) => s.tree)!;
  const s = useMemo(() => treeStats(tree), [tree]);
  const search = (q: string) => {
    setState({ peopleQuery: q });
    setView('people');
  };

  if (!s.people) {
    return (
      <div className="page">
        <div className="page-inner">
          <h1 style={{ fontSize: 22 }}>Reports</h1>
          <p className="muted">Add some people to see facts and charts about your tree.</p>
        </div>
      </div>
    );
  }

  const partnerTotal = s.partnerKinds.reduce((n, c) => n + c.count, 0);
  const socialTotal = s.socialKinds.reduce((n, c) => n + c.count, 0);

  return (
    <div className="page reports">
      <div className="page-inner reports-inner">
        <header>
          <h1 style={{ fontSize: 22 }}>Reports</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            Facts and charts about {tree.name}. Click a bar to see those people.
          </p>
        </header>

        <Overview s={s} />

        {s.facts.length > 0 && (
          <ReportSection title="Highlights">
            <div className="highlight-grid">
              {s.facts.map((f, i) => (
                <Highlight key={i} fact={f} />
              ))}
            </div>
          </ReportSection>
        )}

        <ReportSection title="Who's here">
          <div className="report-grid">
            <ChartCard title="Gender" subtitle={`${num(s.people)} people`} table={s.genders} total={s.people}>
              <HBars data={s.genders} total={s.people} onPick={(c) => search(exactly('gender', c.key.startsWith('custom:') ? c.label : c.key))} />
            </ChartCard>
            <ChartCard title="Pronouns" subtitle="As recorded" table={s.pronouns} total={s.people}>
              {s.pronouns.length === 1 && s.pronouns[0].key === '__none' ? (
                <p className="single-value">
                  No pronouns are recorded yet. Relationship words follow gender until they are.
                </p>
              ) : (
                <HBars data={s.pronouns} total={s.people} onPick={(c) => c.key !== '__other' && search(c.key === '__none' ? 'no:pronouns' : exactly('pronouns', c.label))} />
              )}
            </ChartCard>
            <ChartCard
              title="Relationship words"
              subtitle="Mother, father, or parent"
              table={[
                { key: 'feminine', label: 'Feminine', count: s.terms.feminine },
                { key: 'masculine', label: 'Masculine', count: s.terms.masculine },
                { key: 'neutral', label: 'Neutral', count: s.terms.neutral },
              ]}
              total={s.people}
            >
              <TermsBar terms={s.terms} total={s.people} />
            </ChartCard>
          </div>
        </ReportSection>

        {(enough(s.birthDecades) || enough(s.livingAges) || enough(s.deathAges)) && (
          <ReportSection title="Over time">
            <div className="report-grid">
              {enough(s.birthDecades) && (
                <ChartCard title="Births by decade" subtitle={`${num(s.birthDecades.reduce((n, d) => n + d.count, 0))} people with a birth year`} table={s.birthDecades} wide>
                  <Columns data={s.birthDecades} onPick={(c) => search(`born:=${c.key}s`)} />
                </ChartCard>
              )}
              {enough(s.birthMonths) && (
                <ChartCard title="Birth months" subtitle={`${num(s.birthMonths.reduce((n, d) => n + d.count, 0))} people with an exact birth date`} table={s.birthMonths}>
                  <Columns data={s.birthMonths} />
                </ChartCard>
              )}
              {enough(s.livingAges) && (
                <ChartCard title="Ages of living people" subtitle={`${num(s.livingAges.reduce((n, d) => n + d.count, 0))} people with a birth date`} table={s.livingAges}>
                  <Columns data={s.livingAges} />
                </ChartCard>
              )}
              {enough(s.deathAges) && (
                <ChartCard title="Age at death" subtitle={`${num(s.deathAges.reduce((n, d) => n + d.count, 0))} people with birth and death dates`} table={s.deathAges}>
                  <Columns data={s.deathAges} />
                </ChartCard>
              )}
            </div>
          </ReportSection>
        )}

        {(s.surnames.length > 0 || s.birthplaces.length > 0 || s.occupations.length > 0) && (
          <ReportSection title="Names, places, and work">
            <div className="report-grid">
              {s.surnames.length > 0 && (
                <ChartCard title="Surnames" subtitle="Most common first" table={s.surnames} total={s.people}>
                  <HBars data={s.surnames} total={s.people} onPick={(c) => c.key !== '__other' && search(exactly('surname', c.label))} />
                </ChartCard>
              )}
              {s.birthplaces.length > 0 && (
                <ChartCard title="Birthplaces" subtitle="Most common first" table={s.birthplaces} total={s.people}>
                  <HBars data={s.birthplaces} total={s.people} onPick={(c) => c.key !== '__other' && search(exactly('birthplace', c.label))} />
                </ChartCard>
              )}
              {s.occupations.length > 0 && (
                <ChartCard title="Occupations" subtitle="People with each job" table={s.occupations}>
                  {s.occupations[0].count > 1 ? (
                    <HBars data={s.occupations} onPick={(c) => c.key !== '__other' && search(exactly('occupation', c.label))} />
                  ) : (
                    // Nobody shares a job, so bars would all be the same length: list them instead.
                    <div className="word-chips">
                      {s.occupations.filter((c) => c.key !== '__other').map((c) => (
                        <button key={c.key} className="word-chip" onClick={() => search(exactly('occupation', c.label))} title="See who">
                          {c.label}
                        </button>
                      ))}
                      {s.occupations.at(-1)!.key === '__other' && <span className="faint small-text">and {s.occupations.at(-1)!.label.replace(/^Other \((\d+)\)$/, '$1 more')}</span>}
                    </div>
                  )}
                </ChartCard>
              )}
            </div>
          </ReportSection>
        )}

        {(s.parentLinks.length > 0 || partnerTotal > 0 || socialTotal > 0) && (
          <ReportSection title="How the family connects">
            <div className="report-grid">
              {s.parentLinks.length > 0 && (
                <ChartCard title="Parent links" subtitle="Every parent–child link, by kind" table={s.parentLinks}>
                  <HBars data={s.parentLinks} single={(c) => `All ${num(c.count)} are ${c.label.toLowerCase()}.`} />
                </ChartCard>
              )}
              {partnerTotal > 0 && (
                <ChartCard title="Partnerships" subtitle={`${num(partnerTotal)} recorded, by kind`} table={s.partnerKinds}>
                  <HBars data={s.partnerKinds} single={(c) => (c.key === 'partner' ? `All ${num(c.count)} are recorded as partners, without saying what kind.` : `All ${num(c.count)} are ${c.label.toLowerCase()}.`)} />
                </ChartCard>
              )}
              {socialTotal > 0 && (
                <ChartCard title="Friends and mentors" subtitle={`${num(socialTotal)} links, by kind`} table={s.socialKinds}>
                  <HBars data={s.socialKinds} single={(c) => `All ${num(c.count)} are ${c.label.toLowerCase()}.`} />
                </ChartCard>
              )}
            </div>
          </ReportSection>
        )}

        <ReportSection title="Records">
          <div className="report-grid">
            <section className="card-box chart-card">
              <h2 className="report-title">How complete the records are</h2>
              <div className="faint small-text">Share of people with each detail</div>
              <div className="chart-body meters">
                {s.completeness.map((c) => (
                  <Meter key={c.key} label={c.label} count={c.count} total={s.people} />
                ))}
                {s.deathDates.of > 0 && <Meter label={`Death date (of ${num(s.deathDates.of)} who died)`} count={s.deathDates.known} total={s.deathDates.of} />}
              </div>
            </section>
            <section className={`card-box chart-card${s.checks.length ? ' checks' : ''}`}>
              <h2 className="report-title">
                {s.checks.length > 0 && <AlertTriangle size={15} aria-hidden />}
                Worth checking
              </h2>
              {s.checks.length > 0 ? (
                <>
                  <div className="faint small-text">Dates that contradict each other, so one is probably a typo</div>
                  <ul className="check-list">
                    {s.checks.map((f, i) => (
                      <li key={i}>
                        <span>{f.text}</span>
                        {f.people && f.people.length > 0 && <People ids={f.people} />}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="muted small-text" style={{ marginTop: 6 }}>
                  No dates contradict each other: nobody dies before they're born, is born before a parent, or marries before birth.
                </p>
              )}
            </section>
          </div>
        </ReportSection>
      </div>
    </div>
  );
}

function ReportSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="report-section">
      <h2 className="section-heading">{title}</h2>
      {children}
    </section>
  );
}

function People({ ids }: { ids: Id[] }) {
  return (
    <span className="fact-people">
      {ids.map((id) => (
        <PersonLink key={id} id={id} />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- overview

function Overview({ s }: { s: TreeStats }) {
  const parts = [
    { key: 'living', label: 'living', n: s.living, cls: 'life-living' },
    { key: 'deceased', label: 'deceased', n: s.deceased, cls: 'life-deceased' },
    { key: 'unknown', label: 'not recorded', n: s.notRecorded, cls: 'life-unknown' },
  ].filter((p) => p.n);
  return (
    <div className="stat-row">
      <div className="stat stat-people">
        <div className="stat-label">People</div>
        <div className="stat-value">{num(s.people)}</div>
        <div className="minibar" role="img" aria-label={parts.map((p) => `${p.n} ${p.label}`).join(', ')}>
          {parts.map((p) => (
            <span key={p.key} className={p.cls} style={{ flexGrow: p.n }} />
          ))}
        </div>
        <div className="stat-legend">
          {parts.map((p) => (
            <span key={p.key}>
              <i className={p.cls} />
              {num(p.n)} {p.label}
            </span>
          ))}
        </div>
      </div>
      <Stat label="Generations" value={num(s.generations)} note="longest line, parent to child" />
      <Stat label="Partnerships" value={num(s.partnerships)} note={`${num(s.parentLinks.reduce((n, c) => n + c.count, 0))} parent links`} />
      <Stat label="Groups" value={num(s.groups)} note={s.groups ? 'friend groups, households, and more' : 'none yet'} />
      <Stat label="Surnames" value={num(s.surnameCount)} note={s.surnameCount ? 'different family names' : 'none recorded yet'} />
    </div>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={`stat-value${value.length > 6 ? ' long' : ''}`}>{value}</div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- highlights

const TOPIC_ICONS: Record<FactTopic, LucideIcon> = {
  life: Hourglass,
  birthday: Cake,
  family: Users,
  partners: Heart,
  identity: Rainbow,
  names: Tag,
  groups: UsersRound,
  links: Network,
};

function Highlight({ fact }: { fact: Fact }) {
  const Icon = TOPIC_ICONS[fact.topic ?? 'family'];
  return (
    <div className="highlight" title={fact.text}>
      <div className="highlight-label">
        <Icon size={14} aria-hidden />
        {fact.label}
      </div>
      <div className="highlight-value">{fact.value}</div>
      {fact.note && <div className="highlight-note">{fact.note}</div>}
      {fact.people && fact.people.length > 0 && <People ids={fact.people} />}
    </div>
  );
}

// ---------------------------------------------------------------- charts

/** A chart with a table twin: every value is readable without hovering or telling colors apart. */
function ChartCard({ title, subtitle, children, table, total, wide }: { title: string; subtitle?: string; children: ReactNode; table: Count[]; total?: number; wide?: boolean }) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className={`card-box chart-card${wide ? ' wide' : ''}`}>
      <div className="chart-head">
        <div style={{ minWidth: 0 }}>
          <h2 className="report-title">{title}</h2>
          {subtitle && <div className="faint small-text">{subtitle}</div>}
        </div>
        <button className={`btn icon small ghost${asTable ? ' on' : ''}`} aria-pressed={asTable} onClick={() => setAsTable((x) => !x)} title={asTable ? 'Show chart' : 'Show as table'} aria-label="Show as table">
          <Table2 size={15} />
        </button>
      </div>
      <div className="chart-body">
        {asTable ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{title}</th>
                <th className="num">Count</th>
                {total ? <th className="num">Share</th> : null}
              </tr>
            </thead>
            <tbody>
              {table.map((c) => (
                <tr key={c.key}>
                  <td>{c.label}</td>
                  <td className="num">{num(c.count)}</td>
                  {total ? <td className="num">{pct(c.count, total)}%</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

/** Horizontal bars for one series: label, bar from a shared baseline, value at the tip. */
function HBars({ data, total, onPick, single }: { data: Count[]; total?: number; onPick?: (c: Count) => void; single?: (c: Count) => string }) {
  // One bar compares nothing: say it in words.
  if (single && data.length === 1) return <p className="single-value">{single(data[0])}</p>;
  const max = Math.max(1, ...data.map((d) => d.count));
  // Long labels (full place names) get their own line rather than being cut short.
  const stacked = data.some((d) => d.label.length > 22);
  return (
    <div className={`hbars${stacked ? ' stacked' : ''}`} role="list">
      {data.map((d) => {
        const text = `${d.label}: ${num(d.count)}${total ? ` (${pct(d.count, total)}%)` : ''}`;
        const clickable = onPick && d.key !== '__other';
        const content = (
          <>
            <span className="hbar-label">{d.label}</span>
            <span className="hbar-track">
              {d.count > 0 && <span className="hbar" style={{ width: `${(d.count / max) * 100}%` }} />}
              <span className="hbar-value">
                {num(d.count)}
                {total ? <span className="faint"> · {pct(d.count, total)}%</span> : null}
              </span>
            </span>
          </>
        );
        return clickable ? (
          <button key={d.key} role="listitem" className="hbar-row clickable" title={`${text}. Click to see them.`} onClick={() => onPick(d)}>
            {content}
          </button>
        ) : (
          <div key={d.key} role="listitem" className="hbar-row" title={text}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

/** Part of a whole on a full-width track: how many of everyone have something. */
function Meter({ label, count, total }: { label: string; count: number; total: number }) {
  const p = pct(count, total);
  return (
    <div className="meter" title={`${label}: ${num(count)} of ${num(total)} (${p}%)`}>
      <div className="meter-top">
        <span>{label}</span>
        <span className="meter-value">
          {num(count)} <span className="faint">of {num(total)}</span> <b>{p}%</b>
        </span>
      </div>
      <div className="meter-track" role="meter" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <span style={{ width: `${(count / Math.max(1, total)) * 100}%` }} />
      </div>
    </div>
  );
}

/** The width an element is drawn at, so a chart can lay itself out in real pixels and keep its text readable. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Columns along an ordered axis (decades, months, age bands), with each value written above its column. */
function Columns({ data, onPick }: { data: Count[]; onPick?: (c: Count) => void }) {
  const [ref, measured] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const W = Math.max(200, measured || 320);
  const H = 120;
  const top = 18; // room for the value over the tallest column
  const max = Math.max(1, ...data.map((d) => d.count));
  const slot = W / data.length;
  const bar = Math.max(4, Math.min(36, slot * 0.68));
  // Values over every column when they fit; otherwise only the hovered one, in the readout.
  const labelsFit = slot >= Math.max(...data.map((d) => String(d.count).length)) * 7 + 4;
  const tickWidth = Math.max(...data.map((d) => d.label.length)) * 7 + 12;
  const every = Math.max(1, Math.ceil(tickWidth / slot));
  const h = hover !== null ? data[hover] : null;
  return (
    <div className="columns-chart" ref={ref}>
      {!labelsFit && (
        <div className="col-readout" aria-live="polite">
          {h ? (
            <>
              <b>{h.label}</b>: {num(h.count)} {h.count === 1 ? 'person' : 'people'}
            </>
          ) : (
            <span className="faint">
              <span className="for-mouse">Hover a column for its count</span>
              <span className="for-touch">Tap a column for its count</span>
            </span>
          )}
        </div>
      )}
      <svg width={W} height={H + top + 22} viewBox={`0 ${-top} ${W} ${H + top + 22}`} role="img" aria-label={data.map((d) => `${d.label}: ${d.count}`).join(', ')} onPointerLeave={() => setHover(null)}>
        {data.map((d, i) => {
          const x = i * slot + (slot - bar) / 2;
          const bh = (d.count / max) * H;
          const r = Math.min(4, bh);
          return (
            <g key={d.key} className={onPick ? 'clickable' : undefined} onPointerEnter={() => setHover(i)} onClick={() => onPick?.(d)}>
              <title>{`${d.label}: ${num(d.count)}${onPick ? '. Click to see them.' : ''}`}</title>
              <rect className="hit" x={i * slot} y={-top} width={slot} height={H + top + 22} />
              {bh > 0 && <path className={`col${hover === i ? ' on' : ''}`} d={`M${x},${H} V${H - bh + r} Q${x},${H - bh} ${x + r},${H - bh} H${x + bar - r} Q${x + bar},${H - bh} ${x + bar},${H - bh + r} V${H} Z`} />}
              {labelsFit && d.count > 0 && (
                <text className="col-value" x={x + bar / 2} y={H - bh - 5} textAnchor="middle">
                  {num(d.count)}
                </text>
              )}
              {i % every === 0 && (
                <text className="tick" x={i * slot + slot / 2} y={H + 16} textAnchor="middle">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
        <line className="axis" x1={0} x2={W} y1={H + 0.5} y2={H + 0.5} />
      </svg>
    </div>
  );
}

/** Part-to-whole for the three kinds of relationship words: one stacked bar plus a legend with counts. */
function TermsBar({ terms, total }: { terms: Record<'feminine' | 'masculine' | 'neutral', number>; total: number }) {
  const parts = [
    { key: 'feminine', label: 'Feminine', n: terms.feminine, cls: 's1' },
    { key: 'masculine', label: 'Masculine', n: terms.masculine, cls: 's2' },
    { key: 'neutral', label: 'Neutral', n: terms.neutral, cls: 's3' },
  ];
  return (
    <div>
      <div className="stackbar" role="img" aria-label={parts.map((p) => `${p.label} ${p.n}`).join(', ')}>
        {parts.filter((p) => p.n).map((p) => (
          <span key={p.key} className={`stack-seg ${p.cls}`} style={{ flexGrow: p.n }} title={`${p.label}: ${p.n} (${pct(p.n, total)}%)`} />
        ))}
      </div>
      <div className="legend-col">
        {parts.map((p) => (
          <span key={p.key} className="legend-key">
            <i className={p.cls} />
            <span className="legend-name">{p.label}</span>
            <b>{num(p.n)}</b> <span className="faint">{pct(p.n, total)}%</span>
          </span>
        ))}
      </div>
      <p className="faint small-text" style={{ marginTop: 10 }}>
        Set per person in their editor. Automatic follows pronouns, then gender.
      </p>
    </div>
  );
}
